import React, { useState, useRef, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';
import { CommitmentStepProps } from './types';
import { GoalCommitment } from '../Milestone_organizer/types';
import { MindfulIcon } from '../../icons';

/**
 * CommitmentStep - Stap 4 van de intake flow (VERPLICHT)
 * 
 * Dit is het mentale startpunt. Zonder commitment geen verdere uitwerking.
 * 
 * Elementen:
 * - Vaste commitment-tekst (letterlijk, geen herformulering)
 * - Checkbox: "Ik committeer mij aan dit doel"
 * - Naam (input)
 * - Datum (auto, read-only)
 * - Optioneel: startvideo (max 2 minuten)
 * 
 * Bij bevestiging: status = "committed"
 */

const MAX_RECORDING_TIME = 5 * 60; // 5 minuten in seconden

const CommitmentStep: React.FC<CommitmentStepProps> = ({
  goalTitle,
  userId,
  onCommit,
  onBack
}) => {
  const [name, setName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | undefined>();
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const today = new Date().toISOString().split('T')[0];
  const canCommit = name.trim().length > 0 && confirmed;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Attach stream to video element when recording starts
  useEffect(() => {
    if (isRecording && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play().catch(err => console.warn('Play warning:', err));
    }
  }, [isRecording]);

  const handleCommit = () => {
    if (canCommit) {
      const commitment: GoalCommitment = {
        name: name.trim(),
        date: today,
        confirmed: true,
        videoUrl: videoUrl
      };
      onCommit(commitment);
    }
  };

  // Video recording handlers
  const startRecording = async () => {
    try {
      // Cleanup any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, 
        audio: true 
      });
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.play().catch(err => console.warn('Play warning:', err));
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9') 
          ? 'video/webm;codecs=vp9' 
          : 'video/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setRecordedBlob(blob);
        
        // Local preview URL
        const previewUrl = URL.createObjectURL(blob);
        setVideoPreviewUrl(previewUrl);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.src = previewUrl;
        }
        
        // Stop timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        // Upload to Firebase Storage
        setIsUploading(true);
        try {
          const timestamp = Date.now();
          const videoPath = `commitment-videos/${userId}/${timestamp}.webm`;
          const storageRef = ref(storage, videoPath);
          
          await uploadBytes(storageRef, blob);
          const firebaseUrl = await getDownloadURL(storageRef);
          
          setVideoUrl(firebaseUrl); // This is the permanent URL
          console.log('Video uploaded successfully:', firebaseUrl);
        } catch (err) {
          console.error('Failed to upload video:', err);
          alert('Video uploaden mislukt. Je kunt de video opnieuw opnemen.');
          // Keep the preview URL for playback even if upload fails
        } finally {
          setIsUploading(false);
        }
      };

      // Eerst isRecording true zodat video element rendert
      setIsRecording(true);
      setRecordingTime(0);
      
      // Wacht tot video element gerenderd is
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Nu stream koppelen aan video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(err => console.warn('Play warning:', err));
      }
      
      // Start recording met chunked data (elke seconde) - voorkomt memory issues
      mediaRecorder.start(1000);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_TIME - 1) {
            stopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);

      // Auto-stop after max time (backup)
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, MAX_RECORDING_TIME * 1000);

    } catch (err) {
      console.error('Failed to start recording:', err);
      alert('Kon camera niet starten. Controleer je browser-permissies.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearVideo = () => {
    setVideoUrl(undefined);
    setVideoPreviewUrl(undefined);
    setRecordedBlob(null);
    if (videoRef.current) {
      videoRef.current.src = '';
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Commitment
        </h2>
        <p className="text-gray-600">
          Je staat op het punt om je te committeren aan:
        </p>
        <p className="text-lg font-medium text-orange-600 mt-2">
          "{goalTitle}"
        </p>
      </div>

      {/* Commitment tekst - LETTERLIJK, NIET AANPASSEN */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
        <p className="text-gray-700 leading-relaxed">
          Onderweg zul je voor keuzes komen te staan.
          <br /><br />
          Door het ondertekenen van deze doelstelling geef je jezelf houvast
          om door ups en downs te gaan en stel je jezelf accountable.
          <br /><br />
          Wij staan je bij om je te helpen met acties en vieren samen jouw mijlpalen.
          Je kunt deze reis ook delen met mede-ondernemers.
        </p>
      </div>

      {/* Commitment checkbox */}
      <div className="mb-6">
        <label className="flex items-start gap-3 cursor-pointer group">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500 cursor-pointer"
          />
          <span className="text-gray-900 group-hover:text-orange-600 transition-colors">
            Ik committeer mij aan dit doel
          </span>
        </label>
      </div>

      {/* Naam input */}
      <div className="mb-4">
        <label 
          htmlFor="commitment-name" 
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Je naam
        </label>
        <input
          id="commitment-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Voer je naam in"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
        />
      </div>

      {/* Datum (read-only) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Datum
        </label>
        <input
          type="text"
          value={new Date().toLocaleDateString('nl-NL', { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric' 
          })}
          readOnly
          className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
        />
      </div>

      {/* Visualisatie-kaart - Mentale voorbereiding vóór video opname */}
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 mb-6 border border-amber-200">
        <div className="flex items-start gap-3 mb-4">
          <MindfulIcon size={24} />
          <h3 className="text-lg font-medium text-gray-900">Visualisatie</h3>
        </div>
        
        <p className="text-gray-700 leading-relaxed mb-4">
          Neem even de tijd voordat je op record drukt.
        </p>
        
        <p className="text-gray-900 font-medium mb-3">
          Visualiseer het doel behaald.
        </p>
        
        <ul className="space-y-2 text-gray-700 mb-4">
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">•</span>
            <span>Hoe ziet het eruit?</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">•</span>
            <span>Hoe voelt het?</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">•</span>
            <span>Wat hoor je in je omgeving?</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">•</span>
            <span>Welke mensen zijn om je heen?</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">•</span>
            <span><strong>Wie ben jij geworden?</strong></span>
          </li>
        </ul>
        
        <p className="text-gray-600 italic">
          Ga rustig zitten en neem dit beeld mee voordat je begint met inspreken.
        </p>
      </div>

      {/* Optionele startvideo */}
      <div className="mb-8 p-4 border border-dashed border-gray-300 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">
          Startvideo <span className="text-gray-400">(optioneel, max 5 minuten)</span>
        </h3>
        
        {!videoPreviewUrl && !isRecording && !isUploading && (
          <button
            onClick={startRecording}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-orange-600 border border-gray-300 rounded-lg hover:border-orange-400 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Video opnemen
          </button>
        )}

        {isRecording && (
          <div className="space-y-3">
            <div className="relative">
              <video 
                ref={videoRef} 
                className="w-full rounded-lg bg-black aspect-video"
                muted
                playsInline
                autoPlay
                style={{ transform: 'scaleX(-1)' }}
              />
              <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/50 rounded-full px-3 py-1">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-white text-xs">REC</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-red-500 text-sm">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="font-mono">
                  {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
                </span>
                <span className="text-gray-400">/ 5:00</span>
              </span>
              <button
                onClick={stopRecording}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600 transition-colors"
              >
                Stop opname
              </button>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mb-3" />
            <span className="text-sm text-gray-600">Video uploaden...</span>
          </div>
        )}

        {videoPreviewUrl && !isRecording && !isUploading && (
          <div className="space-y-3">
            <video 
              ref={videoRef}
              src={videoPreviewUrl}
              className="w-full rounded-lg bg-black"
              controls
              playsInline
            />
            <div className="flex items-center justify-between">
              {videoUrl ? (
                <span className="flex items-center gap-2 text-green-600 text-sm">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Video opgeslagen
                </span>
              ) : (
                <span className="text-sm text-orange-600">Upload mislukt - probeer opnieuw</span>
              )}
              <button
                onClick={clearVideo}
                className="text-sm text-gray-500 hover:text-red-500 transition-colors"
              >
                Video verwijderen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-100">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          &larr; Terug
        </button>
        <button
          onClick={handleCommit}
          disabled={!canCommit || isUploading}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            canCommit && !isUploading
              ? 'bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          Onderteken en start
        </button>
      </div>
    </div>
  );
};

export default CommitmentStep;
